import { dropDiagramSvg } from "@/lib/diagramSvg";
import { deleteAssetIfOwnedBy } from "@/lib/idb";
import type { AppDispatch } from "@/store";
import { diagramDocRemoved } from "@/store/diagramSlice";
import { chartDocRemoved } from "@/store/chartSlice";
import { ganttDocRemoved } from "@/store/ganttSlice";
import { griddDocRemoved } from "@/store/griddSlice";
import type { SlideContent } from "@/types/content";

// Drops the out-of-band artifacts a content window owns so removing or replacing it
// never orphans storage (the orphan rule, TODO §3.5/§5.8, generalized to two windows in
// §4.4/§4.6):
//   gridd — the grid body lives in griddSlice; `griddDocRemoved` unlinks it and the
//           persistence listener decides whether the IndexedDB row is actually deleted,
//           against the row's owner key (§17.6.2).
//   image — the blob lives in the `assets` IndexedDB store, never in Redux, so it is deleted
//           here directly — but only if this deck owns it (§17.6.2).
//   diagram — the Excalidraw scene lives in diagramSlice; `diagramDocRemoved` unlinks it and
//           the persistence listener decides the row's fate against its owner key
//           (§21/§4.3.4).
//   gantt — the schedule lives in ganttSlice; `ganttDocRemoved` unlinks it and the same
//           owner-gated delete decides the IndexedDB row's fate (§26/§3.3.4). Nothing else
//           to drop: a schedule holds no blobs and has no render cache.
//   chart — the table and encoding live in chartSlice; `chartDocRemoved` unlinks it and the
//           same owner-gated delete decides the IndexedDB row's fate (§15/§3.3.4). Nothing
//           else to drop: a chart holds no blobs and no render cache.
//   text  — inline on the slide record, so nulling the window is enough; nothing here.
// Shared by Clear Slide, per-window clear, the two→single layout collapse, and §20's
// permanent delete from the archive.
//
// === §17.6.2 / §20.1.1 — THE OWNERSHIP GATE, and why it is stated at this door ===
//
// A knowledge nugget's body is shared: the slide in this deck and the slides in four others
// all point at ONE `griddDocId`, filed under the nugget rather than under any deck (§17.1.3).
// Clearing that window here must unlink it and nothing more. Deleting it is silent data loss
// in every other deck — they keep rendering a body that is no longer in the database, and
// nothing anywhere says what happened.
//
// Every caller above deletes through this one function, which is why the rule is stated here
// and nowhere else: a check added at a call site covers one caller, and the next caller
// written reintroduces the bug. §20 shipped before there was an ownership model and left this
// paragraph as a comment; §21 made it real for diagrams; §17 has now made it real for all
// four stores.
//
// **The unlink is unconditional and the DELETE is gated, which is deliberate.** Unlinking is
// correct either way — this deck is not using the body any more, whoever owns it — so it stays
// synchronous and stays here. The delete cannot: the owner lives in IndexedDB, this function
// is not async, and a check made outside the delete's own transaction is a check made against
// a value that may have changed by the time the delete lands. So each store's gate sits inside
// its delete (`deleteGriddDocIfOwnedBy` and the three beside it), reached from the persistence
// listener for the three Redux-backed bodies and called directly here for the one that is not.
//
// `deckId` is the last argument rather than the first because it was added to four existing
// call sites at once, and a trailing parameter makes the diff say what changed.
export function dropContentArtifacts(
  dispatch: AppDispatch,
  content: SlideContent,
  deckId: string,
): void {
  if (content?.kind === "gridd") {
    dispatch(griddDocRemoved({ docId: content.griddDocId }));
  } else if (content?.kind === "image") {
    // §17.6.2 Gated: an image on a linked slide is the NUGGET's blob, and every other deck
    // using that nugget is rendering it. The gate is inside the delete transaction for the
    // reason given above.
    void deleteAssetIfOwnedBy(content.assetId, deckId);
  } else if (content?.kind === "diagram") {
    dispatch(diagramDocRemoved({ docId: content.diagramDocId }));
    // §5.2 The rendered SVG lives in a module-level cache outside Redux, so it does not fall
    // out of the store update. Dropped HERE rather than at the call site for the same reason
    // everything else in this function is: Clear Slide, the per-window clear, the two→single
    // layout collapse and §20's permanent delete all come through this one door, and a cache
    // eviction added to one of them is a leak in the other three.
    //
    // Evicting a nugget-owned diagram's cached SVG is harmless even though the BODY survives:
    // the cache is a render cache, re-derived on the next mount in whichever deck needs it.
    //
    // The IMAGE BLOBS inside the scene are not dropped here — this function is synchronous
    // and cannot read the body, and they must only go if the body itself really went (a
    // nugget-owned body survives, §17.6.2). That happens in the persistence listener's
    // `diagramDocRemoved` effect, which can see both.
    dropDiagramSvg(content.diagramDocId);
  } else if (content?.kind === "gantt") {
    // §26/§3.3.4 Unlinking is unconditional and correct either way; whether the row itself
    // goes is decided against its owner key inside the delete transaction, exactly as for a
    // diagram body.
    dispatch(ganttDocRemoved({ docId: content.ganttDocId }));
  } else if (content?.kind === "chart") {
    // §15/§3.3.4 The gantt line again, and for the same reasons — including the one that is
    // easy to miss: a URL-sourced chart's refresh timer is pure UI state torn down by the
    // block's own unmount (§5.3.5), so there is nothing timer-shaped to cancel here.
    dispatch(chartDocRemoved({ docId: content.chartDocId }));
  }
}
